import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  StickyNote as StickyIcon,
  Plus,
  Search,
  Pin,
  Archive,
  Clock,
  FolderKanban,
  Filter,
  X,
  Loader2,
  Settings as SettingsIcon,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import NoteCard from './NoteCard';
import NoteModal from './NoteModal';
import NoteContextMenu from './NoteContextMenu';
import NoteSettingsModal from './NoteSettingsModal';
import ConvertToTaskModal from './ConvertToTaskModal';
import { getColor } from './noteColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIEWS = [
  { key: 'all', label: 'All Notes', icon: FolderKanban },
  { key: 'pinned', label: 'Pinned', icon: Pin },
  { key: 'recent', label: 'Recent', icon: Clock },
  { key: 'archive', label: 'Archive', icon: Archive },
];

const StickyNotesBoard = () => {
  const { user, token } = useAuth();
  const [notes, setNotes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterCreator, setFilterCreator] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [activeNote, setActiveNote] = useState(null);
  const [startInEdit, setStartInEdit] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertNote, setConvertNote] = useState(null);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, note }

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const fetchNotes = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('view', view);
      if (selectedCategory) params.append('category', selectedCategory);
      if (search) params.append('search', search);
      if (filterTag) params.append('tag', filterTag);
      if (filterCreator) params.append('created_by', filterCreator);
      const res = await axios.get(`${API}/notes?${params.toString()}`, {
        headers: authHeaders,
      });
      setNotes(res.data.notes || []);
    } catch (e) {
      toast.error('Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [authHeaders, view, selectedCategory, search, filterTag, filterCreator]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notes/categories`, {
        headers: authHeaders,
      });
      setCategories(res.data.categories || []);
    } catch {
      // silent
    }
  }, [authHeaders]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: authHeaders });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch {
      // non-blocking
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchCategories();
    fetchUsers();
  }, [fetchCategories, fetchUsers]);

  useEffect(() => {
    setLoading(true);
    fetchNotes();
  }, [fetchNotes]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Counts for sidebar
  const [counts, setCounts] = useState({ all: 0, pinned: 0, archive: 0 });
  useEffect(() => {
    const loadCounts = async () => {
      try {
        const [all, pinned, archive] = await Promise.all([
          axios.get(`${API}/notes?view=all`, { headers: authHeaders }),
          axios.get(`${API}/notes?view=pinned`, { headers: authHeaders }),
          axios.get(`${API}/notes?view=archive`, { headers: authHeaders }),
        ]);
        setCounts({
          all: all.data.total || 0,
          pinned: pinned.data.total || 0,
          archive: archive.data.total || 0,
        });
      } catch {
        // non-blocking
      }
    };
    loadCounts();
  }, [authHeaders, notes.length]);

  // ============= CRUD handlers =============

  const handleCreateOrUpdate = async (payload, existing) => {
    try {
      if (existing) {
        await axios.put(`${API}/notes/${existing.id}`, payload, {
          headers: authHeaders,
        });
        toast.success('Note updated');
      } else {
        await axios.post(`${API}/notes`, payload, { headers: authHeaders });
        toast.success('Note created');
      }
      setModalOpen(false);
      setActiveNote(null);
      fetchNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save note');
    }
  };

  const handleTogglePin = async (note) => {
    try {
      await axios.post(`${API}/notes/${note.id}/pin`, {}, { headers: authHeaders });
      fetchNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to pin');
    }
  };

  const handleArchive = async (note) => {
    try {
      await axios.post(`${API}/notes/${note.id}/archive`, {}, { headers: authHeaders });
      toast.success('Note archived');
      setModalOpen(false);
      fetchNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive');
    }
  };

  const handleUnarchive = async (note) => {
    try {
      await axios.post(`${API}/notes/${note.id}/unarchive`, {}, { headers: authHeaders });
      toast.success('Note restored');
      setModalOpen(false);
      fetchNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore');
    }
  };

  const handleDelete = async (note) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/notes/${note.id}`, { headers: authHeaders });
      toast.success('Note deleted');
      setModalOpen(false);
      setActiveNote(null);
      fetchNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // ============= Drag & drop =============

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = notes.findIndex((n) => n.id === active.id);
    const newIndex = notes.findIndex((n) => n.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(notes, oldIndex, newIndex);
    setNotes(reordered);

    try {
      const items = reordered.map((n, i) => ({ id: n.id, position: i }));
      await axios.put(`${API}/notes/reorder`, { items }, { headers: authHeaders });
    } catch {
      toast.error('Failed to save order');
      fetchNotes();
    }
  };

  // ============= Derived data =============

  const visibleNotes = notes;

  const openNote = (note, opts = {}) => {
    setActiveNote(note);
    setStartInEdit(!!opts.edit);
    setModalOpen(true);
  };

  const openNewNote = () => {
    setActiveNote(null);
    setStartInEdit(true);
    setModalOpen(true);
  };

  const clearFilters = () => {
    setSelectedCategory(null);
    setFilterTag('');
    setFilterCreator('');
    setSearchInput('');
    setSearch('');
  };

  // ============= Context menu =============
  const openContextMenu = (e, note) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, note });
  };

  const handleContextAction = async (action, note) => {
    if (action === 'edit') {
      openNote(note, { edit: true });
    } else if (action === 'pin') {
      await handleTogglePin(note);
    } else if (action === 'archive') {
      await handleArchive(note);
    } else if (action === 'unarchive') {
      await handleUnarchive(note);
    } else if (action === 'delete') {
      await handleDelete(note);
    } else if (action === 'copy') {
      try {
        await navigator.clipboard.writeText(
          `${note.title}\n\n${note.content || ''}`
        );
        toast.success('Copied to clipboard');
      } catch {
        toast.error('Clipboard unavailable');
      }
    } else if (action === 'summarize') {
      openNote(note, { edit: true });
      // AI button in modal will need a click; we can't trigger it from here
      // without refactoring. Leaving openNote edit-mode so user clicks Summarize.
    } else if (action === 'convert') {
      setConvertNote(note);
      setConvertOpen(true);
    }
  };

  // ============= Convert to task =============
  const handleConvertToTask = async (payload) => {
    if (!convertNote) return;
    try {
      const res = await axios.post(
        `${API}/notes/${convertNote.id}/convert-to-task`,
        payload,
        { headers: authHeaders }
      );
      toast.success('Task created · note archived');
      setConvertOpen(false);
      setConvertNote(null);
      setModalOpen(false);
      setActiveNote(null);
      fetchNotes();
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to convert');
    }
  };

  const hasActiveFilters =
    selectedCategory || filterTag || filterCreator || search;

  return (
    <div className="flex gap-6 min-h-[calc(100vh-80px)]" data-testid="sticky-notes-page">
      {/* ============= LEFT SIDEBAR ============= */}
      <aside className="w-60 flex-shrink-0 space-y-6 hidden md:block">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent flex items-center gap-2">
            <StickyIcon className="text-amber-500" size={24} />
            Sticky Notes
          </h1>
          <p className="text-xs text-slate-500 mt-1">Your thinking space.</p>
        </div>

        {/* Views */}
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-2 mb-1">
            Views
          </p>
          {VIEWS.map((v) => {
            const Icon = v.icon;
            const active = view === v.key;
            const count =
              v.key === 'all'
                ? counts.all
                : v.key === 'pinned'
                ? counts.pinned
                : v.key === 'archive'
                ? counts.archive
                : null;
            return (
              <button
                key={v.key}
                onClick={() => {
                  setView(v.key);
                  setSelectedCategory(null);
                }}
                data-testid={`view-${v.key}`}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                  active
                    ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-900 font-semibold'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Icon size={14} />
                  {v.label}
                </span>
                {count !== null && (
                  <span
                    className={`text-[10px] px-1.5 rounded-full ${
                      active ? 'bg-amber-200 text-amber-800' : 'bg-slate-200 text-slate-600'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Categories */}
        <div className="space-y-1">
          <div className="flex items-center justify-between px-2 mb-1">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Categories
            </p>
            <button
              onClick={() => setSettingsOpen(true)}
              className="text-slate-400 hover:text-slate-600 transition-colors"
              title="Settings"
              data-testid="notes-settings-btn"
            >
              <SettingsIcon size={12} />
            </button>
          </div>
          <button
            onClick={() => setSelectedCategory(null)}
            data-testid="category-all"
            className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-all ${
              !selectedCategory
                ? 'bg-slate-100 text-slate-800 font-semibold'
                : 'text-slate-600 hover:bg-slate-50'
            }`}
          >
            All
          </button>
          {categories.map((c) => {
            const color = getColor(c.color);
            const active = selectedCategory === c.name;
            return (
              <button
                key={c.id}
                onClick={() => setSelectedCategory(c.name)}
                data-testid={`category-${c.name}`}
                className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                  active
                    ? 'bg-slate-100 text-slate-800 font-semibold'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${color.solid}`} />
                {c.name}
              </button>
            );
          })}
        </div>
      </aside>

      {/* ============= MAIN AREA ============= */}
      <main className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search notes..."
              className="pl-9 bg-white"
              data-testid="notes-search-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters((v) => !v)}
            data-testid="notes-filter-toggle"
          >
            <Filter size={14} className="mr-1" /> Filters
          </Button>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              onClick={clearFilters}
              className="text-slate-500"
              data-testid="notes-clear-filters"
            >
              <X size={14} className="mr-1" /> Clear
            </Button>
          )}
          <div className="ml-auto">
            <Button
              onClick={openNewNote}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-md"
              data-testid="new-note-button"
            >
              <Plus size={16} className="mr-1" /> New Note
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="flex flex-wrap gap-3 mb-5 p-3 bg-white rounded-lg border border-slate-200">
            <Input
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              placeholder="Tag (e.g. growth)"
              className="max-w-[180px]"
              data-testid="filter-tag-input"
            />
            <select
              value={filterCreator}
              onChange={(e) => setFilterCreator(e.target.value)}
              className="px-3 py-2 rounded-md border border-slate-200 text-sm bg-white"
              data-testid="filter-creator-select"
            >
              <option value="">Created by: anyone</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name || u.email}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Active view title */}
        <div className="flex items-baseline gap-2 mb-3">
          <h2 className="text-lg font-semibold text-slate-800">
            {VIEWS.find((v) => v.key === view)?.label}
            {selectedCategory && (
              <span className="text-slate-400 font-normal"> / {selectedCategory}</span>
            )}
          </h2>
          <span className="text-xs text-slate-500">
            {visibleNotes.length} note{visibleNotes.length === 1 ? '' : 's'}
          </span>
        </div>

        {/* Board */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="animate-spin text-amber-500" size={32} />
          </div>
        ) : visibleNotes.length === 0 ? (
          <div
            className="text-center py-20 bg-white/60 border-2 border-dashed border-slate-200 rounded-xl"
            data-testid="notes-empty-state"
          >
            <StickyIcon className="mx-auto text-slate-300" size={48} />
            <p className="mt-4 text-slate-500 font-medium">
              {view === 'archive' ? 'No archived notes yet.' : 'No notes yet.'}
            </p>
            {view !== 'archive' && (
              <Button
                variant="link"
                className="text-amber-600 mt-2"
                onClick={openNewNote}
                data-testid="empty-state-create-btn"
              >
                Create your first note
              </Button>
            )}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={visibleNotes.map((n) => n.id)}
              strategy={rectSortingStrategy}
            >
              <div
                className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4"
                data-testid="notes-grid"
              >
                {visibleNotes.map((note) => (
                  <NoteCard
                    key={note.id}
                    note={note}
                    onOpen={openNote}
                    onTogglePin={handleTogglePin}
                    onArchive={handleArchive}
                    onDelete={handleDelete}
                    onContextMenu={openContextMenu}
                    currentUserId={user?.id}
                    readOnly={view === 'archive'}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </main>

      {/* Modal */}
      <NoteModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setActiveNote(null);
          setStartInEdit(false);
        }}
        onSave={handleCreateOrUpdate}
        onDelete={handleDelete}
        onArchive={handleArchive}
        onUnarchive={handleUnarchive}
        onConvertToTask={(note) => {
          setConvertNote(note);
          setConvertOpen(true);
        }}
        onTogglePin={async (note) => {
          await handleTogglePin(note);
          try {
            const res = await axios.get(`${API}/notes/${note.id}`, {
              headers: authHeaders,
            });
            setActiveNote(res.data);
          } catch {
            // ignore
          }
        }}
        initialNote={activeNote}
        categories={categories}
        users={users}
        currentUserId={user?.id}
        token={token}
        startInEdit={startInEdit}
      />

      {/* Settings */}
      <NoteSettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        token={token}
        categories={categories}
        onCategoriesChanged={fetchCategories}
      />

      {/* Convert to Task */}
      <ConvertToTaskModal
        open={convertOpen}
        onClose={() => {
          setConvertOpen(false);
          setConvertNote(null);
        }}
        note={convertNote}
        users={users}
        onConfirm={handleConvertToTask}
      />

      {/* Context menu */}
      {contextMenu && (
        <NoteContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          note={contextMenu.note}
          currentUserId={user?.id}
          onAction={handleContextAction}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};

export default StickyNotesBoard;
